import { Link } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import Footer from "../components/Footer";
import { Building2, Key, Server, User } from "lucide-react";
import PublicNav from "../components/PublicNav";
import { ROLE_CLASSES } from "../constants/statusColors";

function CodeBlock({ code }) {
  return (
    <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
      <pre className="text-sm text-green-400 font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-16">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function ApiDocs() {
  useEffect(() => {
    document.title = "Joborigo: Employer API Documentation";
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <PublicNav sticky />

      <div className="flex-1 flex min-w-0">
        {/* Sidebar */}
        <aside className="hidden lg:block w-56 flex-shrink-0 border-r border-gray-100 dark:border-white/10 px-6 py-10 sticky top-16 self-start">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
            On this page
          </p>
          <nav className="flex flex-col gap-2">
            {[
              { href: "#prerequisites", label: "Prerequisites" },
              { href: "#overview", label: "Overview" },
              { href: "#authentication", label: "Authentication" },
              { href: "#connect-token", label: "Connect token" },
              { href: "#integrate", label: "Link a candidate" },
              { href: "#status-push", label: "Push status" },
              { href: "#correspondence", label: "Correspondence" },
              { href: "#statuses", label: "Status values" },
              { href: "#errors", label: "Errors" },
              { href: "#rate-limits", label: "Rate limits" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
              >
                {item.label}
              </a>
            ))}
            <div className="mt-8 pt-6 border-t border-gray-100 dark:border-white/10">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                Also available
              </p>
              <Link
                to="/employers/arena-api"
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition block"
              >
                Arena API docs →
              </Link>
            </div>
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 max-w-3xl min-w-0 px-6 lg:px-12 py-16">
          <div className="mb-12">
            <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
              Employer API
            </p>
            <h1 className="text-4xl font-extrabold mb-4">API Documentation</h1>
            <p className="text-gray-500 dark:text-gray-400 text-lg leading-relaxed">
              The Joborigo Employer API lets you push real-time status updates
              to candidates directly from your ATS or HR system. Candidates see
              updates immediately in their Joborigo dashboard, with no more ghosting
              by accident.
            </p>
          </div>

          <Section id="prerequisites" title="Prerequisites">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Before integrating with the Joborigo API, make sure the following
              are in place:
            </p>
            <div className="flex flex-col gap-3 mb-6">
              {[
                {
                  icon: Building2,
                  title: "Joborigo employer account",
                  desc: "Register at joborigo.com/employers/register. Free to start.",
                },
                {
                  icon: Key,
                  title: "API key",
                  desc: "Generate one from your Employer Dashboard under API Keys. Treat it like a password. Never expose it in client-side code or public repositories.",
                },
                {
                  icon: Server,
                  title: "Server-side integration",
                  desc: "All API calls must be made server-side (from your backend or ATS). The API key must never appear in browser code or mobile apps.",
                },
                {
                  icon: User,
                  title: "Candidate on Joborigo",
                  desc: "The candidate must have a Joborigo account. If they don't, encourage them to sign up at joborigo.com. Without an account, integration is not possible.",
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-4 h-4 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                      {item.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/20 dark:border-brand-violet-on-dark/20 rounded-xl px-5 py-4">
              <p className="text-sm text-brand-violet dark:text-brand-violet-on-dark font-medium mb-1">
                Base URL
              </p>
              <code className="text-sm font-mono text-gray-700 dark:text-gray-300">
                https://api.joborigo.com/api/v1
              </code>
            </div>
          </Section>

          <Section id="overview" title="Overview">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              The integration flow has two steps: link the candidate, then push
              status updates as their application progresses.
            </p>
            <ol className="flex flex-col gap-3 mb-6">
              {[
                "Generate an API key from your Employer Dashboard",
                "Link a candidate using their connect token, resume slug, or email",
                "Call /status-push whenever their status changes in your ATS",
              ].map((step, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-gray-500 dark:text-gray-400"
                >
                  <span className="w-6 h-6 rounded-full bg-brand-violet text-white text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
          </Section>

          <Section id="authentication" title="Authentication">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              All API endpoints require authentication via an API key in the{" "}
              <code className="bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">
                X-API-Key
              </code>{" "}
              header.
            </p>
            <CodeBlock
              code={`curl -X POST https://api.joborigo.com/api/v1/employer/integrate \\
  -H "X-API-Key: jbr_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"connect_token": "abc123xyz"}'`}
            />
          </Section>

          <Section id="connect-token" title="Candidate connect token">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              The connect token is the recommended way to link a candidate, since it
              requires no email address and is fully privacy-preserving.
            </p>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              How it works
            </h3>
            <div className="flex flex-col gap-3 mb-6">
              {[
                {
                  step: "1",
                  desc: "The candidate generates a connect link from their Joborigo dashboard.",
                },
                {
                  step: "2",
                  desc: "They paste the full link (joborigo.com/connect/token) in the portfolio or website field of their job application.",
                },
                {
                  step: "3",
                  desc: "You or your ATS captures the token from their application.",
                },
                {
                  step: "4",
                  desc: "Your system calls POST /employer/integrate with the token. The token is consumed — it can only be used once.",
                },
                {
                  step: "5",
                  desc: "The integration is created. You can now push status updates to this candidate.",
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="flex gap-3 text-sm text-gray-500 dark:text-gray-400"
                >
                  <span className="w-5 h-5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                    {item.step}
                  </span>
                  {item.desc}
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Token properties
            </h3>
            <div className="flex flex-col gap-2 mb-6">
              {[
                { prop: "Expiry", value: "72 hours from generation" },
                {
                  prop: "Uses",
                  value:
                    "One-time only — consumed on first successful integrate call",
                },
                {
                  prop: "Privacy",
                  value: "Does not expose the candidate's email address",
                },
                {
                  prop: "Format",
                  value: "URL-safe random string, ~32 characters",
                },
              ].map((item) => (
                <div
                  key={item.prop}
                  className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                    {item.prop}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {item.value}
                  </span>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Non-technical employers
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed mb-4">
              If you don't have an ATS integration, you can still connect a
              candidate manually. When you click a candidate's connect link,
              you'll land on a Joborigo page that explains the process. After
              registering or signing in, the token is pre-filled in your
              Employer Settings where you can connect with one click — no API
              call needed.
            </p>
          </Section>

          <Section id="integrate" title="Link a candidate">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Link a candidate to your employer account. This is a one-time call
              per candidate. Joborigo supports three identifier methods. Use
              whichever suits your integration.
            </p>

            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-green-600 px-2 py-1 rounded">
                POST
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /employer/integrate
              </code>
            </div>

            <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Identifier methods
            </h3>
            <div className="flex flex-col gap-3 mb-5">
              {[
                {
                  method: "connect_token",
                  label: "Recommended",
                  color: ROLE_CLASSES.positive,
                  desc: "One-time token generated by the seeker. No email exposure. Expires in 72h.",
                },
                {
                  method: "resume_slug",
                  label: "Preferred for arena",
                  color: ROLE_CLASSES.active,
                  desc: "The seeker's public resume slug (e.g. stefan-asanin-aB3x). No email exposure.",
                },
                {
                  method: "seeker_email",
                  label: "Legacy",
                  color: ROLE_CLASSES.neutral,
                  desc: "Identify by email address. Use only when the other methods are not available.",
                },
              ].map((item) => (
                <div
                  key={item.method}
                  className="p-4 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-sm font-mono text-brand-violet dark:text-brand-violet-on-dark">
                      {item.method}
                    </code>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.color}`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Request body — provide exactly one identifier:
            </p>
            <CodeBlock
              code={`// Method 1 — connect token (recommended)
            {
              "connect_token": "abc123xyz",
              "candidate_ref": "your-internal-id"   // optional
            }

            // Method 2 — resume slug
            {
              "resume_slug": "stefan-asanin-aB3x",
              "candidate_ref": "your-internal-id"   // optional
            }

            // Method 3 — email (legacy)
            {
              "seeker_email": "candidate@example.com",
              "candidate_ref": "your-internal-id"   // optional
            }`}
            />

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Response
            </p>
            <CodeBlock
              code={`{
              "id": "uuid",
              "employer_id": "uuid",
              "application_id": "uuid",
              "candidate_ref": "your-internal-id",
              "created_at": "2026-04-01T10:00:00Z"
            }`}
            />
          </Section>

          <Section id="status-push" title="Push a status update">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Push a status update to a linked candidate. The candidate sees
              this immediately in their Joborigo dashboard.
            </p>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold text-white bg-green-600 px-2 py-1 rounded">
                POST
              </span>
              <code className="text-sm font-mono text-gray-600 dark:text-gray-300">
                /employer/status-push
              </code>
            </div>

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Request body
            </p>
            <CodeBlock
              code={`{
              "seeker_email": "candidate@example.com",  // identify by email
              // OR
              "candidate_ref": "your-internal-id",      // identify by your own ref
              // OR
              "application_id": "joborigo-uuid",        // identify by Joborigo ID

              "status": "Interview",                    // required — see status values
              "note": "Scheduled for Monday 14 April",  // optional — shown to candidate

              "actions": [                              // optional — correspondence items
                {
                  "action_type": "meeting",             // meeting | document_request | assessment | info
                  "title": "First round interview",
                  "description": "Please join us for a technical interview.",
                  "meeting_at": "2026-05-05T10:00:00Z", // ISO 8601, meetings only
                  "duration_minutes": 45,               // optional
                  "attendees": ["Jane Smith (HR)"],     // optional
                  "url": null,                          // optional — link for assessments
                  "deadline": null,                     // optional — ISO 8601
                  "guidelines": null                    // optional — instructions
                }
              ]
            }`}
            />

            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-5 mb-2">
              Python example — ATS webhook handler
            </p>
            <CodeBlock
              code={`import requests

            def on_candidate_status_change(candidate_ref, new_status, note=None, actions=None):
                response = requests.post(
                    "https://api.joborigo.com/api/v1/employer/status-push",
                    headers={
                        "X-API-Key": "jbr_your_api_key_here",
                        "Content-Type": "application/json",
                    },
                    json={
                        "candidate_ref": candidate_ref,
                        "status": new_status,
                        "note": note,
                        "actions": actions or [],
                    }
                )
                return response.json()`}
            />
          </Section>

          <Section id="correspondence" title="Correspondence actions">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Attach structured correspondence to any status push using the{" "}
              <code className="bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded text-xs font-mono">
                actions
              </code>{" "}
              array. Each action appears in the candidate's Status tab as a
              correspondence item they can interact with.
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  type: "meeting",
                  desc: "Meeting invite with date, time, duration and attendees. Candidate gets a Google Calendar link.",
                  fields: "meeting_at, duration_minutes, attendees",
                },
                {
                  type: "document_request",
                  desc: "Request for documents such as references, certificates or ID. Candidate can upload files directly.",
                  fields: "deadline, guidelines",
                },
                {
                  type: "assessment",
                  desc: "Link to an external test or assessment platform.",
                  fields: "url, deadline, guidelines",
                },
                {
                  type: "info",
                  desc: "General information or instructions for the candidate.",
                  fields: "description, url",
                },
              ].map((a) => (
                <div
                  key={a.type}
                  className="p-4 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <p className="text-sm font-mono font-medium text-brand-violet dark:text-brand-violet-on-dark mb-1">
                    {a.type}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {a.desc}
                  </p>
                  <p className="text-xs text-gray-400">
                    Key fields: <span className="font-mono">{a.fields}</span>
                  </p>
                </div>
              ))}
            </div>
          </Section>

          <Section id="statuses" title="Status values">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Use one of the standard Joborigo status values, or pass any custom
              string — it will be displayed as-is to the candidate.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { status: "Applied", desc: "Initial application received" },
                { status: "Screening", desc: "Under initial review" },
                { status: "Interview", desc: "Interview stage" },
                { status: "Technical test", desc: "Assessment or test" },
                { status: "Final interview", desc: "Final round" },
                { status: "Offer received", desc: "Offer extended" },
                { status: "Accepted", desc: "Offer accepted" },
                { status: "Rejected", desc: "Application unsuccessful" },
                { status: "Withdrawn", desc: "Candidate withdrew" },
              ].map((s) => (
                <div
                  key={s.status}
                  className="bg-gray-50 dark:bg-brand-dark-surface rounded-xl p-3 border border-gray-100 dark:border-white/10"
                >
                  <p className="text-sm font-mono font-medium text-brand-violet dark:text-brand-violet-on-dark">
                    {s.status}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              You can also send custom values — e.g. "Background check",
              "Reference check". These display verbatim to the candidate.
            </p>
          </Section>

          <Section id="errors" title="Errors">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              All errors return JSON with a{" "}
              <code className="bg-gray-100 dark:bg-white/10 px-1 rounded text-xs font-mono">
                detail
              </code>{" "}
              field.
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  code: "400",
                  meaning:
                    "Bad request: missing or invalid parameters, or connect token already used",
                },
                {
                  code: "401",
                  meaning: "Unauthorised — invalid or missing API key",
                },
                {
                  code: "404",
                  meaning:
                    "Not found — candidate not on Joborigo, token expired, or integration not found",
                },
                {
                  code: "429",
                  meaning: "Rate limit exceeded — slow down requests",
                },
                {
                  code: "500",
                  meaning: "Server error — contact support@joborigo.com",
                },
              ].map((e) => (
                <div
                  key={e.code}
                  className="flex items-start gap-4 p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <span className="text-sm font-mono font-bold text-gray-700 dark:text-gray-300 w-10 flex-shrink-0">
                    {e.code}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {e.meaning}
                  </span>
                </div>
              ))}
            </div>
          </Section>

          <Section id="rate-limits" title="Rate limits">
            <p className="text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
              Current limits per API key:
            </p>
            <div className="flex flex-col gap-3">
              {[
                {
                  endpoint: "/employer/integrate",
                  limit: "60 requests / minute",
                },
                {
                  endpoint: "/employer/status-push",
                  limit: "60 requests / minute",
                },
              ].map((r) => (
                <div
                  key={r.endpoint}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-brand-dark-surface rounded-xl border border-gray-100 dark:border-white/10"
                >
                  <code className="text-sm font-mono text-brand-violet dark:text-brand-violet-on-dark">
                    {r.endpoint}
                  </code>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {r.limit}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Need higher limits? Contact support@joborigo.com with your use
              case.
            </p>
          </Section>

          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-surface px-8 py-8 text-center">
              <h2 className="text-xl font-bold mb-2">Ready to integrate?</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-5">
                Create a free employer account and generate your first API key
                in minutes.
              </p>
              <Link
                to="/employers/register"
                className="inline-block bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
              >
                Get started
              </Link>
              <p className="text-xs text-gray-400 mt-3">
                Questions?{" "}
                <a
                  href="mailto:support@joborigo.com"
                  className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  support@joborigo.com
                </a>
              </p>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
